import { twMerge } from '@shared/themes';
import type { ComponentProps } from 'react';
import { Select } from '../../../form-controls/Select/Select';
import { LotteryLayout } from '../../../lotteries/LotteryLayout/LotteryLayout';
import { LotteryHeaderContainer } from '../../../LotteryHeaderContainer/LotteryHeaderContainer';
import type { DrawHistoryDropdown } from '../../hooks/useDrawHistory/useDrawHistory';
import type { LotteryDrawResult } from '../../types';
import { LotteryResult } from '../LotteryResult/LotteryResult';
import { ViewLatest } from '../ViewLatest/ViewLatest';

type ResultsDrawHistoryHeaderProps = ComponentProps<typeof LotteryHeaderContainer> & {
  dropdowns: DrawHistoryDropdown[];
  currentResult: LotteryDrawResult & { drawingDate: string };
};

/**
 * Header for the draw-history archive page: cascading date/time dropdowns plus the selected draw numbers.
 */
function ResultsDrawHistoryHeader({ lotteryName, dropdowns, currentResult, className, ...props }: ResultsDrawHistoryHeaderProps) {
  const { numbers, extraNumbers } = currentResult;

  const classes = {
    container: twMerge('ll-grid-container h-auto py-8 xl:py-10', className),
    layout: 'll-container-inner flex flex-col gap-y-4',
    dropdowns: 'gap-2 flex flex-wrap',
    selectWrapper: 'flex-1',
    select: 'border-current/40 focus-visible:border-current data-[state=open]:border-current',
  };

  return (
    <LotteryHeaderContainer {...props} lotteryName={lotteryName} className={classes.container}>
      <LotteryLayout sidePanelContent={<ViewLatest lotteryName={lotteryName} />} className={classes.layout} isHeader>
        <div className={classes.dropdowns} data-testid="draw-history-dropdowns">
          {dropdowns.map(dropdown => (
            <div key={dropdown.id} className={classes.selectWrapper}>
              <Select
                id={dropdown.id}
                data-testid={dropdown.id}
                label=""
                options={dropdown.options}
                value={dropdown.value}
                onChange={dropdown.onChange}
                className={classes.select}
              />
            </div>
          ))}
        </div>
        <LotteryResult result={{ numbers, extraNumbers }} lotteryName={lotteryName} className="light" />
      </LotteryLayout>
    </LotteryHeaderContainer>
  );
}

export { ResultsDrawHistoryHeader };
export type { ResultsDrawHistoryHeaderProps };
